import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Building2, Calendar, MapPin, FileText, Plus, LogOut, Shield,
  Clock, User, CheckCircle2, AlertCircle, Download, ClipboardCheck, X,
  ScrollText, RefreshCw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import WorkerPremiumCard from '../../components/worker/WorkerPremiumCard';
import TenantBrandMark from '../../components/shared/TenantBrandMark';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import { CARE_ROLE_OPTIONS } from '../../config/careRoles';

import { readClientHub } from '../../lib/clientHubRead';

const API = API_BASE;

const COVER_STATUS = {
  sourcing_staff: { label: 'Finding staff', color: 'bg-amber-100 text-amber-800' },
  awaiting_worker_confirmation: { label: 'Awaiting confirmation', color: 'bg-blue-100 text-blue-800' },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-800' },
  on_site: { label: 'On site now', color: 'bg-green-100 text-green-900' },
  shift_complete: { label: 'Shift complete', color: 'bg-slate-100 text-slate-700' },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-600' },
  assigned: { label: 'Assigned', color: 'bg-emerald-50 text-emerald-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-50 text-red-700' },
};

const HOURS_STATUS = {
  awaiting: { label: 'Awaiting your confirmation', color: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Attendance confirmed', color: 'bg-emerald-100 text-emerald-800' },
  disputed: { label: 'Problem reported', color: 'bg-red-100 text-red-700' },
};

const DISPUTE_REASONS = [
  { value: 'wrong_times', label: 'The recorded times look wrong' },
  { value: 'worker_absent', label: 'The worker did not attend' },
  { value: 'worker_different', label: 'A different worker attended' },
  { value: 'shift_not_needed', label: 'This shift was not needed' },
  { value: 'other', label: 'Something else' },
];

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function fmtDay(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
}

function fmtHours(n) {
  if (n == null) return '—';
  return `${Number(n)}h`;
}

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function fmtMoney(n) {
  if (n == null) return '—';
  return `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
}

export default function ClientHubPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('cover');
  const [sectionErrors, setSectionErrors] = useState({});
  const readVersion = useRef(0);
  const [org, setOrg] = useState(null);
  const [stats, setStats] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [recentShifts, setRecentShifts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [terms, setTerms] = useState(null);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hoursModal, setHoursModal] = useState(null); // { shift, mode: 'confirm'|'dispute' }
  const [unpaidBreakMinutes, setUnpaidBreakMinutes] = useState('0');
  const [disputeForm, setDisputeForm] = useState({ reason: 'wrong_times', note: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [requestForm, setRequestForm] = useState({
    role_required: 'Healthcare Assistant',
    quantity: 1,
    care_location_id: '',
    requested_start_at: '',
    requested_end_at: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('clientToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate('/client/login');
      return;
    }
    try {
      setOrg(JSON.parse(localStorage.getItem('clientOrg') || '{}'));
    } catch {
      setOrg({});
    }
  }, [token, navigate]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const version = ++readVersion.current;
    const current = () => readVersion.current === version && localStorage.getItem('clientToken') === token;
    const result = await readClientHub(API, token);
    if (!current()) return;
    if (result.unauthorized) {
      localStorage.removeItem('clientToken');
      localStorage.removeItem('clientOrg');
      localStorage.removeItem('clientContact');
      navigate('/client/login');
      return;
    }
    const setters = { stats: setStats, cover: setShifts, sites: setLocations, invoices: setInvoices, hours: setRecentShifts, terms: setTerms };
    Object.entries(result.data).forEach(([key, value]) => setters[key](value));
    setSectionErrors(result.errors);
    setLoading(false);
  }, [token, navigate]);

  useEffect(() => {
    load();
    return () => { readVersion.current += 1; };
  }, [load]);

  const logout = () => {
    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientOrg');
    localStorage.removeItem('clientContact');
    navigate('/client/login');
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        ...requestForm,
        request_type: 'one_off_shift',
        requested_start_at: requestForm.requested_start_at ? new Date(requestForm.requested_start_at).toISOString() : undefined,
        requested_end_at: requestForm.requested_end_at ? new Date(requestForm.requested_end_at).toISOString() : undefined,
      };
      const res = await fetch(`${API}/client-hub/staffing-requests`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Request sent — Osabea will confirm shortly');
        setRequestForm({ role_required: 'Healthcare Assistant', quantity: 1, care_location_id: '', requested_start_at: '', requested_end_at: '', notes: '' });
        setTab('cover');
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to submit');
      }
    } catch {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadInvoice = async (id, ref) => {
    try {
      const res = await fetch(`${API}/client-hub/invoices/${id}/pdf`, { headers });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${ref || id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download invoice');
    }
  };

  const openHours = (shift, mode) => {
    setUnpaidBreakMinutes(String(shift.hours_confirmation?.unpaid_break_minutes ?? 0));
    setDisputeForm({ reason: 'wrong_times', note: '' });
    setHoursModal({ shift, mode });
  };

  const confirmHours = async () => {
    if (!hoursModal?.shift) return;
    const minutes = Number(unpaidBreakMinutes);
    const recordedHours = Number(hoursModal.shift.hours_confirmation?.recorded_hours);
    if (unpaidBreakMinutes === '' || !Number.isInteger(minutes) || minutes < 0 || minutes > 1440 || minutes / 60 >= recordedHours) {
      toast.error('Enter whole unpaid break minutes, shorter than the recorded attendance');
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/client-hub/shifts/${hoursModal.shift.id}/approve-hours`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ unpaid_break_minutes: minutes }),
      });
      if (res.ok) {
        toast.success('Thank you — attendance confirmed');
        setHoursModal(null);
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not confirm attendance');
      }
    } catch {
      toast.error('Could not confirm attendance');
    } finally {
      setActionLoading(false);
    }
  };

  const submitDispute = async () => {
    if (!hoursModal?.shift) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/client-hub/shifts/${hoursModal.shift.id}/dispute-hours`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: disputeForm.reason, note: disputeForm.note || undefined }),
      });
      if (res.ok) {
        toast.success('Problem reported — Osabea will review it');
        setHoursModal(null);
        load();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not send your report');
      }
    } catch {
      toast.error('Could not send your report');
    } finally {
      setActionLoading(false);
    }
  };

  const acceptTerms = async () => {
    if (!terms?.terms?.id) return;
    setAcceptingTerms(true);
    try {
      const res = await fetch(`${API}/client-hub/terms/accept`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ terms_version_id: terms.terms.id, accepted: true }),
      });
      if (res.ok) {
        toast.success('Terms accepted — thank you');
        const cur = await fetch(`${API}/client-hub/terms/current`, { headers });
        if (cur.ok) setTerms(await cur.json());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not record your acceptance');
        if (res.status === 409) {
          const cur = await fetch(`${API}/client-hub/terms/current`, { headers });
          if (cur.ok) setTerms(await cur.json());
        }
      }
    } catch {
      toast.error('Could not record your acceptance');
    } finally {
      setAcceptingTerms(false);
    }
  };

  const tabs = [
    { id: 'cover', label: 'Cover', icon: Calendar },
    { id: 'hours', label: 'Hours', icon: ClipboardCheck },
    { id: 'sites', label: 'Sites', icon: MapPin },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'terms', label: 'Terms', icon: ScrollText },
    { id: 'request', label: 'Request', icon: Plus },
  ];

  const selectedError = sectionErrors[tab === 'request' ? 'sites' : tab];

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#F0FAF6] via-white to-[#F8FAFA]">
        <div role="status" aria-label="Loading Client Hub" className="animate-spin h-8 w-8 border-2 border-emerald-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0FAF6] via-white to-[#F8FAFA] pb-24" data-testid="client-hub">
      <a href="#client-hub-main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999] focus:rounded-lg focus:bg-white focus:p-3">Skip to dashboard</a>
      <header className="sticky top-0 z-10 border-b border-emerald-900/20 bg-white px-3 py-2 shadow-sm" data-testid="client-compact-masthead">
        <div className="mx-auto flex min-h-[56px] max-w-2xl items-center gap-2">
          <TenantBrandMark orgName={org?.name || 'Your organisation'} logoUrl={org?.logo_url} />
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-heading text-sm font-bold text-slate-900" title={org?.name || 'Your organisation'}>{org?.name || 'Your organisation'}</h1>
            <p className="text-xs font-medium text-emerald-800">Client Hub</p>
          </div>
          <div className="flex shrink-0 gap-1">
            <Button variant="ghost" size="icon" aria-label="Refresh Client Hub" className="min-h-10 min-w-10" onClick={load} disabled={loading}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Log out" className="min-h-10 min-w-10" onClick={logout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main id="client-hub-main" tabIndex={-1} aria-busy={loading} className="max-w-2xl mx-auto px-4 py-4">
        {stats && !sectionErrors.stats && tab === 'cover' && (
          <section className="mb-4 grid grid-cols-3 gap-2 rounded-2xl bg-emerald-800 p-3 text-center text-white" aria-label="Client work summary" data-testid="client-summary">
            <div><p className="text-lg font-bold">{stats.upcoming_shifts}</p><p className="text-xs text-emerald-100">Upcoming</p></div>
            <div><p className="text-lg font-bold">{stats.care_locations}</p><p className="text-xs text-emerald-100">Sites</p></div>
            <div><p className="text-lg font-bold">{stats.invoices_awaiting_payment}</p><p className="text-xs text-emerald-100">Due</p></div>
          </section>
        )}
        {sectionErrors.stats ? <div role="alert" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
          <p>Your summary couldn’t be loaded. You can use the sections that loaded successfully.</p>
          <Button variant="outline" className="mt-3 min-h-11" onClick={load} disabled={loading}>Retry summary</Button>
        </div> : null}
        {selectedError ? <div role="alert" className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm">
          <h2 className="font-semibold">{tab === 'request' ? 'Sites for your request' : tabs.find((item) => item.id === tab)?.label} couldn’t be loaded</h2>
          <p className="mt-2">Try again to see the latest information.</p>
          <Button variant="outline" className="mt-3 min-h-11" onClick={load} disabled={loading}>Try again</Button>
        </div> : null}
        {tab === 'cover' && !selectedError && (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-[#17324D]">Upcoming agency cover</p>
            {shifts.length === 0 ? (
              <WorkerPremiumCard accent="emerald" className="p-8 text-center">
                <Calendar className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
                <p className="text-slate-600 text-sm">No upcoming shifts booked.</p>
                <Button className="mt-4 rounded-xl bg-emerald-700 shadow-sm" size="sm" onClick={() => setTab('request')}>Request staff</Button>
              </WorkerPremiumCard>
            ) : (
              shifts.map((s) => {
                const st = COVER_STATUS[s.cover_status] || COVER_STATUS.assigned;
                return (
                  <WorkerPremiumCard key={s.id} accent="emerald" padding="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <p className="font-semibold text-slate-900">{s.role_required}</p>
                        <p className="text-sm text-slate-600 mt-0.5">{fmtWhen(s.start_at)} – {fmtWhen(s.end_at).split(', ').pop()}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />{s.care_location_name || s.location_text}
                        </p>
                      </div>
                      <span className={`text-[10px] px-2 py-1 rounded-full font-medium whitespace-nowrap ${st.color}`}>{st.label}</span>
                    </div>
                    {s.worker && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-sm font-medium flex items-center gap-2">
                          <User className="h-4 w-4 text-emerald-700" />
                          {s.worker.display_name}
                          <span className="text-slate-500 font-normal">· {s.worker.role}</span>
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {s.worker.badges.map((b) => (
                            <span key={b} className="text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Shield className="h-3 w-3" />{b}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {s.clock_in_at && (
                      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Clocked in {fmtWhen(s.clock_in_at)}
                        {s.clock_out_at && ` · out ${fmtWhen(s.clock_out_at).split(', ').pop()}`}
                      </p>
                    )}
                  </WorkerPremiumCard>
                );
              })
            )}
          </div>
        )}

        {tab === 'hours' && !selectedError && (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-[#17324D]">Confirm recorded attendance</p>
            <p className="text-xs text-slate-500 -mt-1">
              Charges are based on the agreed scheduled shift hours. Confirming attendance simply
              releases the shift for invoicing.
            </p>
            {(() => {
              const cards = recentShifts.filter((s) => s.hours_confirmation);
              if (cards.length === 0) {
                return (
                  <WorkerPremiumCard accent="emerald" className="p-8 text-center">
                    <ClipboardCheck className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
                    <p className="text-slate-600 text-sm">Nothing to confirm right now.</p>
                    <p className="text-slate-400 text-xs mt-1">Completed shifts will appear here once reviewed.</p>
                  </WorkerPremiumCard>
                );
              }
              return cards.map((s) => {
                const hc = s.hours_confirmation;
                const st = HOURS_STATUS[hc.status] || HOURS_STATUS.awaiting;
                return (
                  <WorkerPremiumCard key={s.id} accent="emerald" padding="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <p className="font-semibold text-slate-900">{s.role_required}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3" />{s.care_location_name || s.location_text}
                        </p>
                        {s.worker && (
                          <p className="text-xs text-slate-600 flex items-center gap-1 mt-1">
                            <User className="h-3 w-3 text-emerald-700" />{s.worker.display_name}
                          </p>
                        )}
                      </div>
                      <span className={`text-[10px] px-2 py-1 rounded-full font-medium whitespace-nowrap ${st.color}`}>{st.label}</span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-slate-50 p-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Scheduled</p>
                        <p className="text-sm font-medium text-slate-700 mt-0.5">
                          {fmtTime(hc.scheduled_start_at)}–{fmtTime(hc.scheduled_end_at)}
                        </p>
                        <p className="text-[11px] text-slate-500">{fmtHours(hc.scheduled_hours)} scheduled</p>
                      </div>
                      <div className="rounded-lg bg-emerald-50/60 p-2.5">
                        <p className="text-[10px] uppercase tracking-wide text-emerald-600/80 font-semibold">Recorded attendance</p>
                        <p className="text-sm font-medium text-slate-700 mt-0.5">
                          {fmtTime(hc.recorded_clock_in_at)}–{fmtTime(hc.recorded_clock_out_at)}
                        </p>
                        <p className="text-[11px] text-slate-500">{fmtHours(hc.recorded_hours)} recorded</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">{fmtDay(hc.scheduled_start_at)}</p>
                    {hc.status === 'approved' && (
                      <p className="text-xs text-emerald-700 mt-2">
                        {hc.unpaid_break_minutes || 0} min unpaid break · {fmtHours(hc.billing_quantity_hours)} confirmed for billing
                      </p>
                    )}

                    {hc.status === 'disputed' && (
                      <p className="text-xs text-red-600 mt-2 flex items-start gap-1">
                        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        You reported a problem. Osabea will review and update this shift.
                      </p>
                    )}
                    {hc.status === 'approved' && (
                      <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Confirmed — ready for invoicing.
                      </p>
                    )}

                    {hc.can_confirm && (
                      <div className="mt-3 flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 rounded-xl bg-emerald-700 shadow-sm"
                          onClick={() => openHours(s, 'confirm')}
                          data-testid={`confirm-hours-${s.id}`}
                        >
                          Confirm attendance
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 rounded-xl border-slate-300"
                          onClick={() => openHours(s, 'dispute')}
                          data-testid={`dispute-hours-${s.id}`}
                        >
                          Report a problem
                        </Button>
                      </div>
                    )}
                  </WorkerPremiumCard>
                );
              });
            })()}
          </div>
        )}

        {tab === 'sites' && !selectedError && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Clock-in QR posters are managed by Osabea. Display the poster we provided at reception.</p>
            {locations.length === 0 ? (
              <WorkerPremiumCard accent="emerald" className="p-6 text-center text-sm text-slate-500">
                No sites linked yet — Osabea will set this up before your first shift.
              </WorkerPremiumCard>
            ) : (
              locations.map((loc) => (
                <WorkerPremiumCard key={loc.id} accent="emerald" padding="p-4">
                  <p className="font-semibold">{loc.name}</p>
                  <p className="text-sm text-slate-600 mt-1">{[loc.address_line_1, loc.city, loc.postcode].filter(Boolean).join(', ')}</p>
                  <p className={`text-xs mt-3 flex items-start gap-2 ${loc.clock_in_enabled ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {loc.clock_in_enabled ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
                    {loc.qr_poster_note}
                  </p>
                </WorkerPremiumCard>
              ))
            )}
          </div>
        )}

        {tab === 'invoices' && !selectedError && (
          <div className="space-y-3">
            {invoices.length === 0 ? (
              <WorkerPremiumCard accent="emerald" className="p-6 text-center text-sm text-slate-500">No invoices yet.</WorkerPremiumCard>
            ) : (
              invoices.map((inv) => (
                <WorkerPremiumCard key={inv.id} accent="emerald" padding="p-4" className="flex justify-between items-center gap-3">
                  <div>
                    <p className="font-semibold">{inv.reference}</p>
                    <p className="text-sm text-slate-600">{fmtMoney(inv.total_amount)}</p>
                    <p className="text-xs text-slate-500 capitalize">{inv.status}{inv.due_date ? ` · due ${fmtWhen(inv.due_date).split(',')[0]}` : ''}</p>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-xl" onClick={() => downloadInvoice(inv.id, inv.reference)}>
                    <Download className="h-4 w-4" />
                  </Button>
                </WorkerPremiumCard>
              ))
            )}
          </div>
        )}

        {tab === 'terms' && !selectedError && (
          <div className="space-y-3" data-testid="client-terms-tab">
            <p className="text-sm font-semibold text-[#17324D]">Client terms</p>
            {!terms?.has_current_terms ? (
              <WorkerPremiumCard accent="emerald" className="p-8 text-center">
                <ScrollText className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
                <p className="text-slate-600 text-sm">No client terms have been published yet.</p>
                <p className="text-slate-400 text-xs mt-1">Osabea will notify you when terms are ready to review.</p>
              </WorkerPremiumCard>
            ) : (
              <WorkerPremiumCard accent="emerald" padding="p-5" className="space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-heading text-lg font-bold text-[#17324D]">{terms.terms.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Version {terms.terms.version}
                      {terms.terms.effective_date ? ` · effective ${terms.terms.effective_date}` : ''}
                    </p>
                  </div>
                  {terms.has_accepted ? (
                    <span className="text-[10px] px-2 py-1 rounded-full font-medium whitespace-nowrap bg-emerald-100 text-emerald-800 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Accepted
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-1 rounded-full font-medium whitespace-nowrap bg-amber-100 text-amber-800 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> Action needed
                    </span>
                  )}
                </div>

                {terms.terms.content ? (
                  <div className="max-h-72 overflow-y-auto rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm text-slate-700 whitespace-pre-wrap">
                    {terms.terms.content}
                  </div>
                ) : terms.terms.document_ref ? (
                  <p className="text-sm text-slate-600">
                    The full terms document is available on request (reference: {terms.terms.document_ref}).
                  </p>
                ) : null}

                {terms.has_accepted ? (
                  <div className="rounded-xl bg-emerald-50/70 border border-emerald-100 p-3 text-xs text-emerald-800">
                    Accepted by <strong>{terms.acceptance?.accepted_by_name || terms.acceptance?.accepted_by_email || 'your organisation'}</strong>
                    {terms.acceptance?.accepted_at ? ` on ${fmtWhen(terms.acceptance.accepted_at)}` : ''}.
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500">
                      By accepting, you confirm you are authorised to agree these terms on behalf of your organisation.
                    </p>
                    <Button
                      className="w-full rounded-xl bg-emerald-700 shadow-sm"
                      disabled={acceptingTerms}
                      onClick={acceptTerms}
                      data-testid="accept-terms-btn"
                    >
                      {acceptingTerms ? 'Recording…' : 'Accept terms'}
                    </Button>
                  </div>
                )}
              </WorkerPremiumCard>
            )}
          </div>
        )}

        {tab === 'request' && !selectedError && (
          <WorkerPremiumCard accent="emerald" padding="p-4" className="space-y-4">
          <form onSubmit={submitRequest} className="space-y-4">
            <p className="font-heading text-lg font-bold text-[#17324D]">Request agency staff</p>
            <div className="space-y-2">
              <Label>Role needed</Label>
              <select
                className="w-full h-12 px-3 rounded-md border border-slate-200 bg-white text-base"
                value={requestForm.role_required}
                onChange={(e) => setRequestForm({ ...requestForm, role_required: e.target.value })}
                required
              >
                {CARE_ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500">Standard roles only, so billing and worker matching stay accurate.</p>
            </div>
            {locations.length > 0 && (
              <div className="space-y-2">
                <Label>Site (optional)</Label>
                <select
                  className="w-full h-12 px-3 rounded-md border border-slate-200 bg-white text-base"
                  value={requestForm.care_location_id}
                  onChange={(e) => setRequestForm({ ...requestForm, care_location_id: e.target.value })}
                >
                  <option value="">Any site / not sure</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Shift starts</Label>
                <Input type="datetime-local" className="h-12" value={requestForm.requested_start_at} onChange={(e) => setRequestForm({ ...requestForm, requested_start_at: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Shift ends</Label>
                <Input type="datetime-local" className="h-12" value={requestForm.requested_end_at} onChange={(e) => setRequestForm({ ...requestForm, requested_end_at: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>How many staff?</Label>
              <Input type="number" min={1} max={20} className="h-12" value={requestForm.quantity} onChange={(e) => setRequestForm({ ...requestForm, quantity: Number(e.target.value) })} />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={requestForm.notes} onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })} rows={3} />
            </div>
            <Button type="submit" className="w-full h-12 rounded-xl bg-emerald-700 shadow-sm" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send request to Osabea'}
            </Button>
          </form>
          </WorkerPremiumCard>
        )}
      </main>

      {hoursModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={() => !actionLoading && setHoursModal(null)}>
          <div
            className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-1">
              <h3 className="font-heading text-lg font-bold text-[#17324D]">
                {hoursModal.mode === 'confirm' ? 'Confirm attendance' : 'Report a problem'}
              </h3>
              <button type="button" onClick={() => !actionLoading && setHoursModal(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {(() => {
              const hc = hoursModal.shift.hours_confirmation || {};
              return (
                <>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Confirm the recorded attendance and any unpaid break for client billing.
                    If the times look wrong, report a problem before confirming.
                  </p>
                  <div className="mt-3 rounded-xl border border-slate-100 divide-y divide-slate-100">
                    <div className="flex justify-between px-3 py-2.5">
                      <span className="text-sm text-slate-500">Scheduled</span>
                      <span className="text-sm font-medium text-slate-800">
                        {fmtTime(hc.scheduled_start_at)}–{fmtTime(hc.scheduled_end_at)} · {fmtHours(hc.scheduled_hours)}
                      </span>
                    </div>
                    <div className="flex justify-between px-3 py-2.5">
                      <span className="text-sm text-slate-500">Recorded attendance</span>
                      <span className="text-sm font-medium text-slate-800">
                        {fmtTime(hc.recorded_clock_in_at)}–{fmtTime(hc.recorded_clock_out_at)} · {fmtHours(hc.recorded_hours)}
                      </span>
                    </div>
                  </div>

                  {hoursModal.mode === 'confirm' ? (
                    <>
                    <div className="mt-4 space-y-2">
                      <Label htmlFor="client-unpaid-break">Unpaid break for billing (minutes)</Label>
                      <Input id="client-unpaid-break" type="number" min="0" max="1440" step="1"
                        value={unpaidBreakMinutes} onChange={(e) => setUnpaidBreakMinutes(e.target.value)} />
                      <p className="text-xs text-slate-500">Use 0 if no unpaid break applies. This confirms client billing hours; employee payroll is handled separately.</p>
                      <p className="text-sm font-medium text-emerald-800">
                        Hours to confirm: {fmtHours(Math.max(0, Number(hc.recorded_hours || 0) - Number(unpaidBreakMinutes || 0) / 60))}
                      </p>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Button variant="outline" className="flex-1 rounded-xl" disabled={actionLoading} onClick={() => setHoursModal(null)}>
                        Cancel
                      </Button>
                      <Button className="flex-1 rounded-xl bg-emerald-700 shadow-sm" disabled={actionLoading} onClick={confirmHours} data-testid="confirm-hours-submit">
                        {actionLoading ? 'Confirming…' : 'Confirm attendance'}
                      </Button>
                    </div>
                    </>
                  ) : (
                    <div className="mt-4 space-y-3">
                      <div className="space-y-2">
                        <Label>What is the problem?</Label>
                        <select
                          className="w-full h-12 px-3 rounded-md border border-slate-200 bg-white text-base"
                          value={disputeForm.reason}
                          onChange={(e) => setDisputeForm({ ...disputeForm, reason: e.target.value })}
                        >
                          {DISPUTE_REASONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Add a note (optional)</Label>
                        <Textarea
                          rows={3}
                          value={disputeForm.note}
                          onChange={(e) => setDisputeForm({ ...disputeForm, note: e.target.value })}
                          placeholder="Tell us what happened"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" className="flex-1 rounded-xl" disabled={actionLoading} onClick={() => setHoursModal(null)}>
                          Cancel
                        </Button>
                        <Button className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 shadow-sm" disabled={actionLoading} onClick={submitDispute} data-testid="dispute-hours-submit">
                          {actionLoading ? 'Sending…' : 'Send report'}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      <nav aria-label="Client navigation" className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-200/80 shadow-[0_-8px_30px_rgba(15,92,94,0.06)] safe-area-pb">
        <div className="max-w-2xl mx-auto flex">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                aria-current={tab === t.id ? 'page' : undefined}
                onClick={() => setTab(t.id)}
                className={`flex-1 py-3 flex flex-col items-center gap-0.5 text-xs ${active ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}
              >
                <Icon className={`h-5 w-5 ${active ? 'text-emerald-700' : ''}`} />
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
